package ui.device.detail.preview

import androidx.compose.runtime.*
import api.LanguageType
import common.frontend.api.*
import device.DevicePlaylist
import provider.*
import tk.mallumo.compose.navigation.LocalNavigation
import tk.mallumo.compose.navigation.LocalNavigationArgs
import tk.mallumo.compose.navigation.Navigation
import tk.mallumo.compose.navigation.viewmodel.viewModel
import tools.EFieldList
import ui.device.detail.DeviceArgs


interface DevicePreviewScope {

    val vm: DevicePreviewVM
    val action: (DevicePreviewVM.Action) -> Unit

    val args: DeviceArgs
    val nav: Navigation
    val login: Io.User
    val display: CommonDisplayProvider
    val lang: LanguageType
    val externals: CommonExternalsProvider


    companion object {
        @Composable
        fun rememberScope(): DevicePreviewScope {

            val args = LocalNavigationArgs.current.rememberArgs(DeviceArgs::class)
            val account = LocalAccountProvider.current
            val display = LocalDisplayProvider.current
            val nav = LocalNavigation.current
            val externals = LocalExternalsProvider.current


            val vm = nav.viewModel(DevicePreviewVM::class, args.deviceId.toString())
            val action = DevicePreviewVM.Action.rememberAction(vm)

            val locale = LocalLocalization.current
            return remember {
                object : DevicePreviewScope {
                    override val vm: DevicePreviewVM
                        get() = vm
                    override val action: (DevicePreviewVM.Action) -> Unit
                        get() = action
                    override val args: DeviceArgs
                        get() = args
                    override val nav: Navigation
                        get() = nav
                    override val login: Io.User
                        get() = account.current!!
                    override val display: CommonDisplayProvider
                        get() = display
                    override val lang: LanguageType
                        get() = locale.language
                    override val externals: CommonExternalsProvider
                        get() = externals

                }.also {
                    vm.init(it)
                }
            }
        }
    }

    class Wrapper(
        val data: Io.DeviceInfo,
        login: Io.User,
        apps: Io.ListingApp,
        playlist: DevicePlaylist,
        val isSuperAdmin: Boolean = login.isSuperAdmin(),
        val canEdit: Boolean = login.canDeviceEdit(data.company.id),
        val canDelete: Boolean = login.canDeviceDelete(data.company.id),
        val canViewCompany: Boolean = login.canCompanyView(data.company.id),
        val canViewLocation: Boolean = login.canLocationView(data.company.id),
        val canViewLayerTop: Boolean = login.canLayerTopView(data.company.id),
        val canViewLayer: Boolean = canViewLayerTop || login.canLayerBottomView(data.company.id),
        val canViewDisplay: Boolean = login.canDeviceDisplayView(data.company.id),
        val canViewHW: Boolean = login.canHardwareView(),
        val canViewResources: Boolean = login.canResourceView(data.company.id),
    ) {
        val adapterPlaylist = WrapperPlaylist(playlist)
        val adapterApps = WrapperApps(apps.apply {
            items.sortBy { it.position }
        })
    }

    class WrapperPlaylist(
        playlist: DevicePlaylist,
        val commercialCount: Int = playlist.campaigns.count { it.isCommercial },
        private var originOrder: List<Long> = playlist.campaigns.map { it.campaignId },
        private val changeInternal: MutableState<Boolean> = mutableStateOf(false)
    ) : EFieldList<DevicePlaylist.Campaign, WrapperPlaylist.Item>(
        items = playlist.campaigns,
        onBindEntry = { Item(it) },
        onChange = {
            val newOrder = it.map { it.data.campaignId }
            changeInternal.value = newOrder != originOrder
        }) {

        override val change by changeInternal

        var uploadingChanges by mutableStateOf(false)
            private set

        override fun applyChanges() {
            originOrder = state.map { it.data.campaignId }
            changeInternal.value = false
            uploadingChanges = false
        }

        fun extract(): Io.ListingPlaylistOrder {
            uploadingChanges = true
            return state.mapIndexed { index, entry ->
                Io.ListingPlaylistOrder.Item(entry.data.campaignId, index + 1)
            }.let { idsOrder ->
                Io.ListingPlaylistOrder(idsOrder)
            }
        }

        fun revokeChanges() {
            uploadingChanges = false
        }

        class Item(
            override val data: DevicePlaylist.Campaign,
            override val key: String = data.campaignId.toString(),
        ) : Entry<DevicePlaylist.Campaign>
    }

    class WrapperApps(
        val apps: Io.ListingApp,
        private var originOrder: List<Long> = apps.items.map { it.id },
        private val changeInternal: MutableState<Boolean> = mutableStateOf(false)
    ) : EFieldList<Io.ListingApp.Item, WrapperApps.Item>(
        items = apps.items,
        onBindEntry = { Item(it) },
        onChange = {
            val newOrder = it.map { it.data.id }
            changeInternal.value = newOrder != originOrder
        }) {

        override val change by changeInternal

        var uploadingChanges by mutableStateOf(false)
            private set

        override fun applyChanges() {
            originOrder = state.map { it.data.id }
            changeInternal.value = false
            uploadingChanges = false
        }


        fun extract(): List<Long> {
            uploadingChanges = true
            return state.map { item -> item.data.id }
        }

        fun revokeChanges() {
            uploadingChanges = false
        }

        class Item(
            override val data: Io.ListingApp.Item,
            override val key: String = data.id.toString(),
        ) : Entry<Io.ListingApp.Item>
    }
}
